import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { recorderApi } from '../../api/recorderAssignmentApi';
import { PageHeader } from '../../components/PageHeader';
import { COMPETITION_STATUS_LABELS, type CompetitionStatus } from '../../types/domain';
import { formatDateCz } from '../../utils/dateFormat';

const STATUS_COLOR: Record<CompetitionStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default',
  LIVE: 'error',
  FINISHED: 'success',
};

export const RecorderHomePage = () => {
  const { data: comps = [], isLoading } = useQuery({
    queryKey: ['recorder', 'competitions'],
    queryFn: recorderApi.myCompetitions,
  });

  return (
    <>
      <Helmet>
        <title>Soutěže | Zapisovatel</title>
      </Helmet>
      <PageHeader title="Přiřazené soutěže" subtitle="Soutěže, kde můžete zadávat výsledky" />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : comps.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              Nemáte přiřazené žádné soutěže. Kontaktujte admina jednotky.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {comps.map((c) => (
            <Card
              key={c.id}
              component={RouterLink}
              to={`/recorder/competitions/${c.id}`}
              variant="outlined"
              sx={{ textDecoration: 'none', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6">{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateCz(c.competitionDate)} {c.location && `• ${c.location}`}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={COMPETITION_STATUS_LABELS[c.status]} color={STATUS_COLOR[c.status]} />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {c.shortCode}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </>
  );
};
